import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { addConfigurator, resetConfigurator } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { makeStyles } from 'tss-react/mui'
import useShopSavedSystemConfiguration from '../../hooks/use-shop-saved-system-configuration'
import systemConfigurationFromDtoToModelTransformer from '../../api/transformer/save-configuration-from-dto-to-model.transformer'
import { useNavigate } from 'react-router-dom'
import { SystemConfigureType } from '../../types'
import { DesiredShippingDate } from '../../models'

interface FavoriteSystemCardProps {
  configuratorId: string
  configuratorName: string
  desiredShippingDate?: DesiredShippingDate
  imageUrl: string
  onConfiguratorDelete: (configuratorId: string) => void
}

const useStyles = makeStyles()(theme => ({
  productImage: {
    margin: 0,
    padding: 0,
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    marginBottom: '12px',
  },
  card: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    border: '1px solid transparent',
    flexShrink: 0,
    padding: 0,
    paddingBottom: theme.spacing(2),
    position: 'relative',
    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.grey.grey05}`,
    },
  },
  title: {
    textAlign: 'left',
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  button: {
    fontSize: '16px',
    padding: '16px 12px',
  },
}))

const MONTH_OFFSET = 1

/*
 * This is the card component for each product on favorite saved configurator.
 */
const FavoriteSystemCard: React.FC<FavoriteSystemCardProps> = ({
  configuratorId,
  desiredShippingDate,
  configuratorName,
  onConfiguratorDelete,
  imageUrl,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const { getSystemConfiguration } = useShopSavedSystemConfiguration()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const handleConfiguratorLoading = (configuratorId: string): void => {
    setIsLoading(true)
    dispatch(resetConfigurator())
    getSystemConfiguration(configuratorId).then(result => {
      if (result) {
        const month = desiredShippingDate?.month != null ? desiredShippingDate.month - MONTH_OFFSET : undefined
        const year = desiredShippingDate?.year

        const savedSystemConfiguration =
          month !== null && year !== null
            ? systemConfigurationFromDtoToModelTransformer(result, month, year)
            : systemConfigurationFromDtoToModelTransformer(result)

        dispatch(addConfigurator(savedSystemConfiguration))
        navigate(`/shop/system-configure/${SystemConfigureType.NormalSystem}`)
      }
    })
  }

  return (
    <Card className={classes.card}>
      <CardMedia className={classes.productImage} component="img" image={imageUrl} alt={t('shop.favorite-system.image-alt')} />
      <Typography className={classes.title}>{configuratorName}</Typography>
      <Box className={classes.buttonContainer}>
        <Button
          className={classes.button}
          fullWidth
          size="large"
          color="inherit"
          variant="outlined"
          onClick={() => {
            onConfiguratorDelete(configuratorId)
          }}
        >
          {t('shop.favorite-system.delete-button')}
        </Button>
        <ButtonWithLoading
          fullWidth
          className={classes.button}
          isLoading={isLoading}
          color="primary"
          variant="contained"
          label={t('shop.favorite-system.select-button')}
          onClick={() => {
            handleConfiguratorLoading(configuratorId)
          }}
        />
      </Box>
    </Card>
  )
}

export default FavoriteSystemCard
